/**
 * The quest progress bar (2026-09-01).
 *
 * User: "For quest completion, we need to add a quest completion progress
 * bar."
 *
 * The hub's quest list reported progress as a bare `3/10` chip at the end of
 * the row. That is the number, but it is not a sense of how close you are —
 * and "how close am I" is the only question a quest list is really asked. A
 * bar answers it before the numbers are read; the numbers stay beside it,
 * because `9/10` and `90/100` feel different and a bar alone cannot say which.
 *
 * A string builder rather than a DOM builder because the hub renders its
 * panels with `innerHTML`, and pure so the arithmetic can be tested — the
 * interesting cases here are all the degenerate ones (a zero target, progress
 * past the target) that a live UI would only show you by looking wrong.
 */

import { amountMarkup } from './currency';
import { t, N_ } from '../i18n/i18n';

/**
 * How a quest *kind* is dressed in the list (2026-10-09): its wax seal, the
 * colour of its row's edge, and the word on its badge. Copper and a candle
 * for the daily, blue and a moon for the weekly, gold and a horned skull for
 * a milestone — so the three read apart before a word of them is.
 * Anything else (SEASONAL, or a type added later) wears the milestone's gold
 * rather than rendering without a seal.
 */
export interface QuestKindStyle { seal: string; accent: string; label: string }
const QUEST_KINDS: Record<string, QuestKindStyle> = {
  DAILY: { seal: '/assets/ui/quests/seal-daily.png', accent: '#d97a45', label: N_('Daily') },
  WEEKLY: { seal: '/assets/ui/quests/seal-weekly.png', accent: '#6f8fe0', label: N_('Weekly') },
  MILESTONE: { seal: '/assets/ui/quests/seal-milestone.png', accent: '#e0b25f', label: N_('Milestone') },
};
export function questKindStyle(questType: string): QuestKindStyle {
  const known = QUEST_KINDS[questType];
  if (known) return { ...known, label: t(known.label) };
  return {
    ...QUEST_KINDS.MILESTONE,
    // An unknown server type has no catalogue entry; it is shown as sent.
    label: questType ? questType.charAt(0) + questType.slice(1).toLowerCase() : t('Quest'),
  };
}

/** Brass while you are working on it, green once it is done. */
const IN_PROGRESS_TINT = 'bg-[#e0a640]';
const COMPLETE_TINT = 'bg-[#5ec24a]';

/**
 * How full the bar is, 0–100.
 *
 * Clamped at both ends. Over-shoot is real: `trackAction` increments by the
 * amount of the action, so extracting 4 items against a "extract 3 items"
 * quest lands on 4/3, and a bar wider than its own track would break the row.
 * A target of 0 (or a missing one) reads as empty rather than as `Infinity`
 * or `NaN`, either of which renders as a blank `width` and a bar stuck full.
 */
export function questPercent(progress: number, targetCount: number): number {
  if (!(targetCount > 0)) return 0;
  if (!Number.isFinite(progress) || progress <= 0) return 0;
  return Math.min(100, (progress / targetCount) * 100);
}

/** The count beside the bar, never showing more than the target was asking for. */
export function questCountLabel(progress: number, targetCount: number): string {
  const shown = Math.max(0, Math.min(progress, targetCount));
  return `${shown}/${targetCount}`;
}

/**
 * The bar's markup, styled to match the rest of the cartoon hub: ink outline,
 * capsule ends, a gloss highlight over the fill.
 */
export function questBarMarkup(progress: number, targetCount: number, completed: boolean): string {
  const pct = questPercent(progress, targetCount);
  const tint = completed ? COMPLETE_TINT : IN_PROGRESS_TINT;
  return `
    <div class="flex items-center gap-2">
      <div class="meter flex-1 h-2.5">
        <div class="meter-fill absolute inset-y-0 left-0 ${tint}" style="width:${pct}%"></div>
      </div>
      <span class="text-[11px] text-text-dim tabular-nums shrink-0">${questCountLabel(progress, targetCount)}</span>
    </div>
  `;
}

/**
 * What a quest pays, with the glyphs (2026-09-05).
 *
 * User: *"Need to add quest rewards. Its not seen when quest is not completed
 * yet."*
 *
 * The hub's quest row showed a name, a description and a progress bar, and put
 * the reward *inside the Claim button* — so the only moment a player could
 * find out what a quest was worth was after they had already done it. That is
 * the one moment the number cannot change anything. "Warden Slain" pays 300 XP
 * and "Rattling Locks" pays 25; which of the five to chase this evening is the
 * question the list is open to answer, and the list was withholding the answer.
 *
 * Returns `''` rather than a placeholder when a quest pays nothing at all — a
 * `SEASONAL` def scores 0 XP today (`QUEST_REWARD_XP`, Tallow has no seasons
 * yet) and "+0 XP" reads as a bug rather than as nothing.
 *
 * One function for every place a quest reward is drawn, so the promise on the
 * row, the amount on the Claim button and the line in the mid-run banner
 * cannot disagree about which currencies a quest can pay.
 */
export function questRewardMarkup(
  quest: { rewardXp?: number; rewardSoft?: number; rewardPremium?: number },
  opts: { hideLabel?: boolean; tone?: string } = {},
): string {
  const parts: string[] = [];
  // Only non-zero rewards. Four extraction quests pay Wax as of 2026-09-06
  // (`quest-defs.ts` WAX_REWARD, and quests.md's amendment for why only those
  // four); Ember is never paid by any quest. The Quest row carries all three
  // columns and an admin can set them, so all three are rendered when present.
  if (quest.rewardXp) parts.push(amountMarkup('xp', quest.rewardXp, { prefix: '+', ...opts }));
  if (quest.rewardSoft) parts.push(amountMarkup('wax', quest.rewardSoft, { prefix: '+', ...opts }));
  if (quest.rewardPremium) parts.push(amountMarkup('ember', quest.rewardPremium, { prefix: '+', ...opts }));
  // Spaced, because unspaced it reads as punctuation: a quest paying XP and
  // Wax rendered as "+150 XP.<coin> +60 Wax", and the dot looked like the full
  // stop at the end of the XP amount rather than a separator between two.
  return parts.join('<span class="text-text-dim mx-1">·</span>');
}
